import { BookingFieldGroup } from "types";

type VisibleFieldMap = Record<
  string,
  { isBookingVisible?: boolean } | undefined
>;

/** Tariff-shaped groups placed on the documents step (German label from API). */
export const TARIFF_GROUP_NAME_DE_AS_DOCUMENTS = "Ihre Dokumente";

/** Tariff-shaped groups placed on the legal step (German label from API). */
export const TARIFF_GROUP_NAME_DE_AS_LEGAL_INFO = "Rechtliches";

/**
 * Apply flow: which booking_field_group_type values belong on each step.
 * Mirrors page-apply-1 … page-apply-3 routing of booking sections.
 */
export const BOOKING_APPLY_PAGE_FIELD_GROUPS: Record<
  1 | 2 | 3,
  readonly string[]
> = {
  1: ["insured_entity", "policy_holder", "tariff", "broker_info"],
  2: ["payment_method", "payer"],
  3: ["documents", "legal_info"],
};

const ALLOWED: Record<1 | 2 | 3, ReadonlySet<string>> = {
  1: new Set(BOOKING_APPLY_PAGE_FIELD_GROUPS[1]),
  2: new Set(BOOKING_APPLY_PAGE_FIELD_GROUPS[2]),
  3: new Set(BOOKING_APPLY_PAGE_FIELD_GROUPS[3]),
};

/**
 * Effective apply-page type: certain `tariff` groups are routed like `documents` / `legal_info`.
 */
export function getEffectiveApplyPageFieldGroupType(
  form: BookingFieldGroup,
): string {
  if (form.booking_field_group_type === "tariff") {
    if (form.name_de === TARIFF_GROUP_NAME_DE_AS_DOCUMENTS) {
      return "documents";
    }
    if (form.name_de === TARIFF_GROUP_NAME_DE_AS_LEGAL_INFO) {
      return "legal_info";
    }
  }
  return form.booking_field_group_type;
}

/**
 * Store / validity / API booking_fields key: same pattern as tariffs (`tariff` + id)
 * for groups that can appear multiple times (`legal_info`, `documents`).
 */
export function getBookingFieldGroupStoreKey(form: BookingFieldGroup): string {
  if (
    form.booking_field_group_type === "tariff" ||
    form.booking_field_group_type === "legal_info" ||
    form.booking_field_group_type === "documents"
  ) {
    return `${form.booking_field_group_type}${form.id}`;
  }
  return form.booking_field_group_type;
}

/** Pinia `bookingFieldsValidity` keys to enforce when leaving an apply step. */
export function getValidityFormKeysForApplyPage(
  forms: BookingFieldGroup[],
  page: 1 | 2 | 3,
): Set<string> {
  return new Set(
    filterBookingFieldGroupsForApplyPage(forms, page).map((f) =>
      getBookingFieldGroupStoreKey(f),
    ),
  );
}

export function filterBookingFieldGroupsForApplyPage(
  forms: BookingFieldGroup[],
  page: 1 | 2 | 3,
): BookingFieldGroup[] {
  const set = ALLOWED[page];
  return forms.filter((f) => set.has(getEffectiveApplyPageFieldGroupType(f)));
}

function coerceBookingFieldCheckboxValue(rawValue: unknown): boolean {
  if (typeof rawValue === "boolean") return rawValue;
  if (typeof rawValue === "string") {
    const normalized = rawValue.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  if (typeof rawValue === "number") return rawValue === 1;
  if (rawValue && typeof rawValue === "object") {
    if ("id" in (rawValue as Record<string, unknown>)) {
      const id = String((rawValue as { id: unknown }).id)
        .trim()
        .toLowerCase();
      return id === "true" || id === "1" || id === "yes";
    }
    if ("value" in (rawValue as Record<string, unknown>)) {
      return Boolean((rawValue as { value: unknown }).value);
    }
  }
  return Boolean(rawValue);
}

function normalizeBookingFieldValueForContractPayload(
  raw: unknown,
  field: { field_type?: string },
): unknown {
  if (field.field_type === "boolean") {
    return coerceBookingFieldCheckboxValue(raw);
  }
  if (
    raw &&
    typeof raw === "object" &&
    field.field_type !== "file" &&
    !Array.isArray(raw)
  ) {
    const value = raw as Record<string, unknown>;
    const hasId = Object.prototype.hasOwnProperty.call(value, "id");
    const hasLabel = Object.prototype.hasOwnProperty.call(value, "label");
    if (!hasId && !hasLabel && Object.keys(value).length === 0) {
      return { id: "", label: "" };
    }
    if (hasId || hasLabel) {
      const id = hasId && value.id != null ? value.id : "";
      const label = hasLabel && value.label != null ? value.label : "";
      return { id, label };
    }
  }
  return raw;
}

/**
 * ID-keyed field objects per group for confirm-booking, same shape as tariff in
 * `booking_fields` (`{ [groupKey]: { [fieldId]: value } }`). One entry per page-3
 * legal / documents group (incl. tariff-routed), keyed by `getBookingFieldGroupStoreKey`.
 */
export function buildApplyPageLegalDocumentsBookingFields(
  forms: BookingFieldGroup[],
  page: 1 | 2 | 3,
  $state: Record<string, unknown> | undefined,
  visibleBookingFields: Record<string, unknown>,
): Record<string, Record<string, unknown>> {
  if (!$state) return {};

  const out: Record<string, Record<string, unknown>> = {};

  for (const form of filterBookingFieldGroupsForApplyPage(forms, page)) {
    const eff = getEffectiveApplyPageFieldGroupType(form);
    if (eff !== "legal_info" && eff !== "documents") continue;

    const formKey = getBookingFieldGroupStoreKey(form);
    const visRaw = visibleBookingFields[formKey];
    if (!visRaw || typeof visRaw !== "object" || Array.isArray(visRaw)) {
      continue;
    }
    const vis = visRaw as VisibleFieldMap;

    const slice = $state[formKey];
    if (
      slice === null ||
      slice === undefined ||
      typeof slice !== "object" ||
      Array.isArray(slice)
    ) {
      continue;
    }
    const stateSlice = slice as Record<string, unknown>;

    const visibleFieldNames: string[] = [];
    for (const key of Object.keys(vis)) {
      if (vis[key]?.isBookingVisible) visibleFieldNames.push(key);
    }

    const fields = (form.app_booking_fields || []).filter((f) =>
      visibleFieldNames.includes(f.field_name),
    );

    const groupFields: Record<string, unknown> = {};
    for (const field of fields) {
      if (!Object.prototype.hasOwnProperty.call(stateSlice, field.field_name)) {
        continue;
      }
      const value = normalizeBookingFieldValueForContractPayload(
        stateSlice[field.field_name],
        field,
      );
      groupFields[String(field.id)] = value;
    }
    if (Object.keys(groupFields).length) {
      out[formKey] = groupFields;
    }
  }

  return out;
}

/** Read a boolean booking field from forms on an apply step (e.g. page 3 legal/documents). */
export function readApplyPageFieldBoolean(
  forms: BookingFieldGroup[],
  page: 1 | 2 | 3,
  $state: Record<string, unknown> | undefined,
  fieldName: string,
): boolean {
  if (!$state) return false;
  for (const form of filterBookingFieldGroupsForApplyPage(forms, page)) {
    const hasField = (form.app_booking_fields || []).some(
      (f) => f.field_name === fieldName,
    );
    if (!hasField) continue;
    const formKey = getBookingFieldGroupStoreKey(form);
    const slice = $state[formKey];
    const raw =
      slice !== null &&
      slice !== undefined &&
      typeof slice === "object" &&
      !Array.isArray(slice)
        ? (slice as Record<string, unknown>)[fieldName]
        : undefined;
    return coerceBookingFieldCheckboxValue(raw);
  }
  return false;
}

/** Page 3 apply-step booleans for confirm-booking payload (field_name from CMS). */
export function resolvePromotionalContactOptIn(
  forms: BookingFieldGroup[],
  $state: Record<string, unknown> | undefined,
): boolean {
  return readApplyPageFieldBoolean(forms, 3, $state, "promotional_contact");
}

export function isBookingFieldGroupOnApplyPage(
  form: BookingFieldGroup,
  page: 1 | 2 | 3,
): boolean {
  return ALLOWED[page].has(getEffectiveApplyPageFieldGroupType(form));
}

/** Documents / legal API groups (incl. tariff-shaped): stay editable on step‑3 payment recap, etc. */
export function isApplyDocumentsLegalEditableGroup(
  form: BookingFieldGroup,
): boolean {
  const eff = getEffectiveApplyPageFieldGroupType(form);
  return eff === "documents" || eff === "legal_info";
}

/** @deprecated Use isApplyDocumentsLegalEditableGroup */
export function isApplyPageThreeEditableGroup(
  form: BookingFieldGroup,
): boolean {
  return isApplyDocumentsLegalEditableGroup(form);
}
